/**
 * /recap/:year — annual recap. Family-wide aggregation of what
 * happened this year: photo count, prayers, answered prayers, journal
 * + gratitude counts, memory verse weeks, reading-plan days, top
 * month for photos. Cover photo lands as a hero at the top.
 *
 * Year switcher at the bottom lets the family browse past recaps. We
 * default to the current year, but accept any 4-digit year in the URL.
 */
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { photoBytesUrl } from '../lib/api';
import {
  recapYearQueryOptions,
  recapYearsQueryOptions,
  type RecapResponse,
} from '../lib/queries';
import { ScreenHeader } from '../components/ScreenHeader';
import { IconButton } from '../components/IconButton';
import { IconArrowLeft } from '../components/icons';

const MONTH_LABEL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export default function RecapPage() {
  const { year: rawYear } = useParams<{ year: string }>();
  const navigate = useNavigate();
  const year = useMemo(() => {
    const n = Number(rawYear);
    if (Number.isInteger(n) && n >= 1900 && n <= 9999) return n;
    return new Date().getFullYear();
  }, [rawYear]);

  const recap = useQuery(recapYearQueryOptions(year));
  const years = useQuery(recapYearsQueryOptions());

  return (
    <>
      <ScreenHeader
        title="Recap"
        right={<span className="text-soft text-sm pb-1">{year}</span>}
      />
      <div className="px-5 pt-3 pb-8">
        <div className="-ml-2 mb-1">
          <IconButton
            icon={<IconArrowLeft />}
            label="Back"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate('/today');
            }}
          />
        </div>

        {recap.isLoading ? (
          <p className="text-soft text-sm py-10 text-center">Loading…</p>
        ) : recap.error || !recap.data ? (
          <p className="text-soft text-sm py-10 text-center">Couldn't load this recap.</p>
        ) : (
          <RecapBody data={recap.data} />
        )}

        {years.data && years.data.years.length > 1 && (
          <div className="mt-8 pt-4 border-t border-line">
            <p className="kicker text-soft mb-2">Other years</p>
            <div className="flex flex-wrap gap-2">
              {years.data.years
                .filter((y) => y !== year)
                .map((y) => (
                  <button
                    key={y}
                    onClick={() => navigate(`/recap/${y}`)}
                    className="h-9 px-4 rounded-full border border-line text-sm text-ink hover:bg-line/40"
                  >
                    {y}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function RecapBody({ data }: { data: RecapResponse }) {
  const topMonthLabel = data.topMonthByPhotos
    ? (MONTH_LABEL[data.topMonthByPhotos.month - 1] ?? '—')
    : null;
  const empty =
    data.photoCount === 0 &&
    data.prayerCount === 0 &&
    data.journalEntries === 0 &&
    data.gratitudeEntries === 0 &&
    data.memoryVerseWeeksCompleted === 0 &&
    data.readingPlanDaysCompleted === 0;

  if (empty) {
    return (
      <p className="text-soft italic text-center py-10">
        No family activity in {data.year} yet. Come back as you fill it in.
      </p>
    );
  }

  return (
    <>
      {data.coverPhotoId && (
        <img
          src={photoBytesUrl(data.coverPhotoId, 'large')}
          alt=""
          className="w-full aspect-video object-cover rounded-2xl bg-line mb-5"
          loading="eager"
        />
      )}

      <h1 className="font-serif text-3xl text-ink leading-tight">
        Your {data.year} together
      </h1>
      {topMonthLabel && (
        <p className="kicker text-sage mt-1">
          Busiest month: {topMonthLabel} · {data.topMonthByPhotos!.count} photos
        </p>
      )}

      <div className="mt-6 grid grid-cols-2 gap-3">
        <Stat label="Photos" value={data.photoCount} />
        <Stat label="Prayers" value={data.prayerCount} />
        <Stat label="Prayers answered" value={data.prayersAnswered} accent="sage" />
        <Stat label="Journal entries" value={data.journalEntries} />
        <Stat label="Gratitude entries" value={data.gratitudeEntries} />
        <Stat label="Memory verses memorized" value={data.memoryVerseWeeksCompleted} />
        <Stat label="Reading-plan passages" value={data.readingPlanDaysCompleted} />
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'sage' | 'oxblood';
}) {
  const color =
    accent === 'sage' ? 'text-sage' : accent === 'oxblood' ? 'text-oxblood' : 'text-ink';
  return (
    <div className="card p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className={`font-serif text-2xl ${color} mt-0.5`}>{value}</div>
    </div>
  );
}
